import {
  Dialog as MUIDialog,
  DialogTitle,
  DialogContent,
  Stack,
  Typography,
  Link,
} from '@mui/material';

import Button from '../../../components/styledComponents/Button';
import colors from '../../../constants/colors';
import useAnalytics from '../../../hooks/useAnalytics';
import { RemoveButton } from '../../styledComponents/IconButtons';

interface CancelTAndCDialogProps {
  open: boolean;
  closeDialog: () => void;
  handleAccept: () => void;
  isLoading?: boolean;
}

const CancelTAndCDialog = ({
  closeDialog,
  handleAccept,
  open,
  isLoading = false,
}: CancelTAndCDialogProps) => {
  const { track } = useAnalytics();
  return (
    <MUIDialog
      aria-labelledby="cancelTermsAndConditionsLabel"
      data-testid="cancelTAndCDialog"
      open={open}
      onClose={closeDialog}
      sx={{ backdropFilter: 'blur(5px)', zIndex: 1500 }}
      PaperProps={{
        sx: {
          borderRadius: 4,
          margin: 0,
          px: { mobile: 3, tablet: 4 },
          position: { mobile: 'fixed', tablet: 'relative' },
          width: { mobile: 1, tablet: 480 },
          height: 'auto',
          maxHeight: 1,
        },
      }}
    >
      <Stack
        justifyContent="flex-end"
        flexDirection="row"
        sx={{
          width: 1,
          mt: { mobile: 2, tablet: 3 },
        }}
      >
        <RemoveButton
          aria-label="Close terms and conditions modal"
          data-testid="closeIconPaperlessAlertDialogButton"
          onClick={closeDialog}
          autoFocus
        />
      </Stack>
      <DialogTitle
        id="cancelTermsAndConditionsLabel"
        sx={{
          fontSize: 28,
          fontWeight: 700,
          textAlign: 'center',
          color: colors.csaaBlack,
          lineHeight: '40px',
          p: 0,
          mt: { mobile: 2, tablet: 3 },
          mb: 3,
        }}
      >
        Review and accept terms and conditions
      </DialogTitle>
      <DialogContent
        sx={{
          pb: 5,
          px: 0,
        }}
      >
        <Stack sx={{ mb: { mobile: 3, tablet: 5 } }}>
          <Typography
            sx={{
              fontSize: { mobile: 16, tablet: 18 },
              fontWeight: 400,
              fontFamily: 'Cabin',
              color: colors.csaaDark,
            }}
          >
            Are you sure you want to cancel without accepting the terms and
            conditions? To finish enrolling in paperless, accept the{' '}
            <Link
              href="https://csaa-insurance.aaa.com/content/aaa-ie/b2c/en/misc/terms-of-use.html"
              target="_blank"
              onClick={() =>
                track('Terms and Conditions Accessed', {
                  event_type: 'Link Accessed',
                  link: 'paperless preferences terms and conditions',
                  location: 'Modal',
                })
              }
              underline="none"
              sx={{
                color: 'primary',
                fontWeight: { mobile: 400, tablet: 700 },
                fontSize: { mobile: 16, tablet: 18 },
              }}
            >
              paperless preferences terms and conditions
            </Link>
          </Typography>
        </Stack>
        <Stack
          direction="column"
          justifyContent="flex-end"
          spacing={{ mobile: 1, tablet: 2 }}
        >
          <Button
            fullWidth
            variant="outlined"
            color="inherit"
            data-testid="paperlessPreferencesUnenrollCancelButton"
            sx={{
              borderWidth: 2,
              fontWeight: 700,
              textTransform: 'none',
              fontSize: 18,
              color: 'text.secondary',
              px: 4,
              py: 0.5,
            }}
            disableRipple
            onClick={closeDialog}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            data-testid="paperlessPreferencesUnenrollDialogButton"
            variant="contained"
            sx={{
              whiteSpace: 'nowrap',
              height: { mobile: 36, tablet: 42 },
            }}
            type="submit"
            onClick={handleAccept}
            loading={isLoading}
          >
            Accept
          </Button>
        </Stack>
      </DialogContent>
    </MUIDialog>
  );
};

export default CancelTAndCDialog;
